<template>
  <div class="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-6 sm:space-y-8">
    <!-- Title -->
    <h1 class="text-3xl sm:text-4xl font-bold text-gray-800">Find Wrestler</h1>

    <!-- Search Input -->
    <div class="relative">
      <div class="flex items-center gap-2 bg-gray-100 rounded-xl p-4 sm:p-6">
        <svg
          class="w-6 h-6 text-gray-500 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
          />
        </svg>
        <input
          ref="searchInput"
          v-model="searchQuery"
          data-kiosk-scan-target
          type="text"
          placeholder="Search name or scan ID card"
          class="flex-1 min-w-0 bg-transparent text-base md:text-xl text-gray-500 placeholder-gray-500 outline-none border-none ring-0 focus:ring-0 font-medium"
          @input="handleSearch"
          autofocus
        />
      </div>
    </div>

    <!-- Wrestler Grid -->
    <div v-if="wrestlers.length > 0" class="grid grid-cols-1 gap-4">
      <button
        v-for="wrestler in wrestlers"
        :key="wrestler.id"
        @click="$emit('select', wrestler)"
        class="wrestler-row flex items-center p-3 sm:p-4 border-2 border-gray-200 rounded-lg transition text-left group"
        @mouseenter="
          $event.currentTarget.style.borderColor = 'var(--team-primary)'
        "
        @mouseleave="$event.currentTarget.style.borderColor = ''"
      >
        <!-- Avatar -->
        <img
          v-if="wrestler.avatar_thumbnail"
          :src="wrestler.avatar_thumbnail"
          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover mr-3 sm:mr-4 flex-shrink-0"
          alt=""
        />
        <span
          v-else
          class="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gray-200 mr-3 sm:mr-4 flex items-center justify-center flex-shrink-0"
        >
          <svg
            class="w-6 h-6 sm:w-8 sm:h-8 text-gray-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clip-rule="evenodd"
            />
          </svg>
        </span>

        <!-- Name & Info -->
        <span class="flex-1 min-w-0 flex flex-col">
          <span class="font-semibold text-gray-900 text-base sm:text-lg break-words">
            {{ wrestler.full_name }}
          </span>
          <span
            v-if="wrestler.age || wrestler.weight_class"
            class="text-sm text-gray-500 mt-0.5 sm:mt-1"
          >
            <template v-if="wrestler.weight_class"
              >{{ wrestler.weight_class }} lbs</template
            >
            <template v-if="wrestler.age && wrestler.weight_class">
              ·
            </template>
            <template v-if="wrestler.age"
              >{{ wrestler.age }} years old</template
            >
          </span>
        </span>

        <!-- Select Button -->
        <span
          class="px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base flex-shrink-0 ml-2 sm:ml-4"
          style="background-color: var(--team-primary); color: var(--team-text)"
        >
          Select
        </span>
      </button>
    </div>

    <!-- Error State -->
    <div v-else-if="searchError" class="text-center py-8">
      <svg
        class="mx-auto h-12 w-12 text-red-400 mb-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <p class="text-red-600 font-medium mb-2">Error searching wrestlers</p>
      <p class="text-gray-500 text-base sm:text-sm">
        {{ searchError }}
      </p>
    </div>

    <!-- No Results State -->
    <div
      v-else-if="searchQuery && wrestlers.length === 0 && !loading"
      class="text-center py-8"
    >
      <p class="text-gray-500">
        No wrestlers found matching "<span class="font-semibold">{{
          searchQuery
        }}</span
        >"
      </p>
    </div>

    <!-- Welcome Message (iOS kiosk only, when idle) -->
    <div
      v-else-if="showWelcome"
      class="ios-welcome text-center py-6 sm:py-8"
    >
      <img
        v-if="teamLogoUrl"
        :src="teamLogoUrl"
        alt="Team logo"
        class="h-20 w-20 sm:h-24 sm:w-24 object-contain mx-auto mb-4"
      />
      <h2
        class="text-2xl sm:text-3xl font-bold text-balance"
        style="color: var(--team-primary)"
      >
        Welcome to {{ teamName }}
      </h2>
      <p class="text-base text-gray-500 mt-2">Search your name or scan your ID card to check in</p>
    </div>

    <!-- Recent Check-ins (shown when idle on any platform) -->
    <div v-if="isIdle && recentCheckIns.length > 0">
      <h3 class="text-base sm:text-lg font-semibold text-gray-700 mb-3">Recent Check-ins</h3>
      <div class="space-y-2">
        <div
          v-for="checkIn in recentCheckIns"
          :key="checkIn.id"
          class="flex items-center p-3 bg-gray-50 rounded-lg"
        >
          <!-- Avatar -->
          <img
            v-if="checkIn.profile && checkIn.profile.avatar_thumbnail"
            :src="checkIn.profile.avatar_thumbnail"
            class="w-10 h-10 rounded-full object-cover mr-3 flex-shrink-0"
            alt=""
          />
          <span
            v-else
            class="w-10 h-10 rounded-full bg-gray-200 mr-3 flex items-center justify-center flex-shrink-0"
          >
            <svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clip-rule="evenodd"
              />
            </svg>
          </span>

          <!-- Name & Event -->
          <span class="flex-1 min-w-0 flex flex-col">
            <span class="font-semibold text-gray-900 truncate text-base sm:text-sm">
              {{ checkIn.profile.display_name }}
            </span>
            <span v-if="checkIn.event_name" class="text-sm sm:text-xs text-gray-500 truncate">
              {{ checkIn.event_name }}
            </span>
          </span>

          <!-- Time & Check Icon -->
          <span class="flex items-center gap-2 flex-shrink-0 ml-3">
            <span class="text-sm sm:text-xs text-gray-400 tabular-nums">{{ formatTime(checkIn.created_at) }}</span>
            <svg class="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KioskWrestlerSearch',
  emits: ['select', 'search-active'],
  props: {
    store: {
      type: Object,
      default: null,
    },
    teamName: {
      type: String,
      default: '',
    },
    teamLogoUrl: {
      type: String,
      default: '',
    },
    warpSpeed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      wrestlers: [],
      loading: false,
      searchTimeout: null,
      searchError: null,
    };
  },
  mounted() {
    // Focus input when component is mounted (handles both initial load and conditional re-render)
    // Use setTimeout to ensure DOM is fully ready, especially after setInterval-triggered navigation
    setTimeout(() => {
      if (this.$refs.searchInput) {
        this.$refs.searchInput.focus();
      }
    }, 100);
  },
  beforeDestroy() {
    // Reset search-active state so parent unhides the camera tile when leaving this screen
    this.$emit('search-active', false);
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
  },
  watch: {
    searchQuery(newVal) {
      // Signal whether user is actively typing a query — lets the kiosk hide the
      // native camera tile while searching (they're clearly not about to scan a card).
      this.$emit('search-active', Boolean(newVal));
    },
  },
  computed: {
    isIOSKiosk() {
      const wrapper = document.getElementById('kiosk-vue-wrapper');
      return wrapper?.classList.contains('ios') && wrapper?.classList.contains('kiosk');
    },
    showWelcome() {
      // Show welcome only in iOS kiosk mode when idle (no query, no results, no error)
      // Hidden in warp speed mode so keyboard doesn't cover recent check-ins
      return !this.warpSpeed
             && this.isIOSKiosk
             && this.teamName
             && !this.searchQuery
             && this.wrestlers.length === 0
             && !this.searchError;
    },
    isIdle() {
      return !this.searchQuery
        && this.wrestlers.length === 0
        && !this.searchError;
    },
    recentCheckIns() {
      if (!this.store || !this.store.state.recentCheckIns) return [];
      return this.store.state.recentCheckIns.slice(0, 5);
    },
  },
  methods: {
    focusInput() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus();
        }
      });
    },
    resetAndFocus() {
      // Clear search state and focus input - useful after check-in completes
      this.searchQuery = '';
      this.wrestlers = [];
      this.searchError = null;
      this.focusInput();
    },
    handleSearch() {
      // Clear any pending search
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Clear results and error if empty query
      if (!this.searchQuery) {
        this.wrestlers = [];
        this.loading = false;
        this.searchError = null;
        return;
      }

      this.loading = true;
      this.searchError = null;

      // Debounce the search
      this.searchTimeout = setTimeout(() => {
        const queryAtRequest = this.searchQuery;
        const url = this.$apiService.checkInWrestlerSearchUrl(this.searchQuery);

        axios
          .get(url)
          .then((response) => {
            // Only update if query hasn't changed since request
            if (this.searchQuery === queryAtRequest) {
              this.wrestlers = response.data.wrestlers || [];
              this.loading = false;
              this.searchError = null;
            }
          })
          .catch((error) => {
            console.error('Failed to search wrestlers:', error);
            if (this.searchQuery === queryAtRequest) {
              this.loading = false;
              this.searchError = this.parseError(error);
            }
          });
      }, 200);
    },
    formatTime(timestamp) {
      return moment(timestamp).format('h:mm a');
    },
    parseError(error) {
      // Network error (no response received - likely offline)
      if (!error.response) {
        if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
          return 'Please check your internet connection and try again';
        }
        return error.message || 'An unexpected error occurred';
      }

      // Server responded with an error
      const { status } = error.response;
      const { data } = error.response;

      // Try to get error message from response
      if (data?.error) {
        return data.error;
      }
      if (data?.errors && Array.isArray(data.errors)) {
        return data.errors.join(', ');
      }

      // Fallback based on status code
      if (status === 401 || status === 403) {
        return 'Session expired. Tap Account below to sign out, then sign back in.';
      }
      if (status === 404) {
        return 'Search service not found, please check your internet connection or email support@wrestlingiq.com if this issue persists.';
      }
      if (status >= 500) {
        return 'Server error. Email support@wrestlingiq.com if this issue persists.';
      }

      return `Request failed (${status})`;
    },
  },
};
</script>
